// Passkey (WebAuthn) client for the main app. Backed by:
//   GET    /passkey/available            is the feature switched on server-side
//   POST   /login/passkey/begin|finish   usernameless sign-in (no session yet)
//   GET    /passkeys                     the signed-in user's passkeys
//   POST   /passkeys/register/begin|finish
//   DELETE /passkeys/{id}
// (backend/workflows/auth/passkey_*.wsl, login_passkey_*.wsl). The server speaks
// WebAuthn JSON with base64url binary fields; this file converts to and from
// the ArrayBuffers navigator.credentials wants.
import { readCsrfCookie, reportHttpStatus } from './dataApi';

const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';

export interface PasskeyInfo {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt?: string;
  synced: boolean;
}

export type PasskeyResult<T = unknown> = { ok: true; data: T } | { ok: false; error: string; cancelled?: boolean };

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

export function b64urlToBuffer(value: string): ArrayBuffer {
  const pad = '='.repeat((4 - (value.length % 4)) % 4);
  const bin = atob((value + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type Descriptor = { id: string; type: string; transports?: string[] };

function decodeDescriptors(list: Descriptor[] | undefined): PublicKeyCredentialDescriptor[] | undefined {
  if (!list || list.length === 0) return undefined;
  return list.map((d) => ({ ...d, id: b64urlToBuffer(d.id) }) as PublicKeyCredentialDescriptor);
}

export function decodeCreationOptions(raw: any): PublicKeyCredentialCreationOptions {
  const pk = raw.publicKey ?? raw;
  return {
    ...pk,
    challenge: b64urlToBuffer(pk.challenge),
    user: { ...pk.user, id: b64urlToBuffer(pk.user.id) },
    excludeCredentials: decodeDescriptors(pk.excludeCredentials),
  };
}

export function decodeRequestOptions(raw: any): PublicKeyCredentialRequestOptions {
  const pk = raw.publicKey ?? raw;
  return {
    ...pk,
    challenge: b64urlToBuffer(pk.challenge),
    allowCredentials: decodeDescriptors(pk.allowCredentials),
  };
}

export function encodeCredential(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const response: Record<string, unknown> = { clientDataJSON: bufferToB64url(r.clientDataJSON) };
  if (r.attestationObject) {
    response.attestationObject = bufferToB64url(r.attestationObject);
    if (typeof r.getTransports === 'function') response.transports = r.getTransports();
  }
  if (r.authenticatorData) {
    response.authenticatorData = bufferToB64url(r.authenticatorData);
    response.signature = bufferToB64url(r.signature);
    if (r.userHandle) response.userHandle = bufferToB64url(r.userHandle);
  }
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response,
  };
}

async function call(path: string, method: string, payload?: unknown): Promise<PasskeyResult<any>> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = readCsrfCookie();
    if (csrf && method !== 'GET') headers['X-CSRF-Token'] = csrf;
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers,
      body: payload === undefined ? undefined : JSON.stringify(payload),
    });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    reportHttpStatus(response.status);
    const data = body?.data ?? body;
    if (response.ok && data && typeof data === 'object' && !data.error) return { ok: true, data };
    return { ok: false, error: data?.error ?? body?.error ?? `Request failed (${response.status})` };
  } catch {
    return { ok: false, error: 'Could not reach the server. Is the backend running?' };
  }
}

// DOMException names the user can trigger by simply dismissing the prompt.
function describe(err: unknown, fallback: string): { error: string; cancelled: boolean } {
  const name = (err as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return { error: 'Passkey request was cancelled or timed out.', cancelled: true };
  }
  if (name === 'SecurityError') {
    return {
      error: `Passkeys are not set up correctly for ${window.location.hostname}: the server's passkey domain (PASSKEY_RP_ID) must be ${window.location.hostname} or a parent domain.`,
      cancelled: false,
    };
  }
  if (name === 'InvalidStateError') return { error: 'This device already has a passkey for your account.', cancelled: false };
  return { error: err instanceof Error && err.message ? err.message : fallback, cancelled: false };
}

export async function fetchPasskeyAvailable(): Promise<boolean> {
  if (!passkeysSupported()) return false;
  const result = await call('/passkey/available', 'GET');
  return result.ok && result.data.available === true;
}

// Whole usernameless sign-in. On success the backend has set the session
// cookie (same as /login), so the caller only needs to flip its logged-in
// state (and cache the returned email).
export async function signInWithPasskey(): Promise<PasskeyResult<{ email: string }>> {
  if (!passkeysSupported()) return { ok: false, error: 'This browser does not support passkeys.' };
  const begin = await call('/login/passkey/begin', 'POST', {});
  if (!begin.ok) return begin;
  let cred: PublicKeyCredential | null;
  try {
    cred = (await navigator.credentials.get({ publicKey: decodeRequestOptions(begin.data.options) })) as PublicKeyCredential | null;
  } catch (err) {
    return { ok: false, ...describe(err, 'Passkey sign-in failed.') };
  }
  if (!cred) return { ok: false, error: 'No passkey was selected.', cancelled: true };
  const finish = await call('/login/passkey/finish', 'POST', {
    ceremonyId: begin.data.ceremonyId,
    credential: encodeCredential(cred),
  });
  if (!finish.ok) return finish;
  // The session payload carries the account's email (set from the profile
  // at token-mint time), which the store uses to key the profile sync.
  return { ok: true, data: { email: typeof finish.data.email === 'string' ? finish.data.email : '' } };
}

// Adds a passkey to the signed-in account.
export async function registerPasskey(label: string): Promise<PasskeyResult<PasskeyInfo>> {
  if (!passkeysSupported()) return { ok: false, error: 'This browser does not support passkeys.' };
  const begin = await call('/passkeys/register/begin', 'POST', { label });
  if (!begin.ok) return begin;
  let cred: PublicKeyCredential | null;
  try {
    cred = (await navigator.credentials.create({ publicKey: decodeCreationOptions(begin.data.options) })) as PublicKeyCredential | null;
  } catch (err) {
    return { ok: false, ...describe(err, 'Could not create a passkey.') };
  }
  if (!cred) return { ok: false, error: 'No passkey was created.', cancelled: true };
  const finish = await call('/passkeys/register/finish', 'POST', {
    ceremonyId: begin.data.ceremonyId,
    credential: encodeCredential(cred),
  });
  if (!finish.ok) return finish;
  return { ok: true, data: finish.data.passkey as PasskeyInfo };
}

export async function listPasskeys(): Promise<PasskeyInfo[] | null> {
  const result = await call('/passkeys', 'GET');
  if (!result.ok) return null;
  return Array.isArray(result.data.passkeys) ? (result.data.passkeys as PasskeyInfo[]) : [];
}

export async function removePasskey(id: string): Promise<PasskeyResult> {
  return call(`/passkeys/${encodeURIComponent(id)}`, 'DELETE');
}

// A readable default name for a new passkey, e.g. "Chrome on Mac".
export function defaultPasskeyLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'device';
  return `${browser} on ${os}`;
}
